import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Shield, Target, TrendingDown, Info } from 'lucide-react';

const MarginOfSafetyCalculator = () => {
  const [intrinsicValue, setIntrinsicValue] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');

  const result = useMemo(() => {
    const ivNum = parseFloat(intrinsicValue);
    const priceNum = parseFloat(currentPrice);
    
    if (!ivNum || ivNum <= 0) {
      return null;
    }
    
    // Calculate margin of safety price (60% of intrinsic value = 40% margin)
    const maxBuyPrice = ivNum * 0.6;
    const marginPercent = 40;
    
    // If current price provided, calculate actual margin
    let actualMargin = null;
    let isPricedRight = null;
    if (priceNum && priceNum > 0) {
      actualMargin = ((ivNum - priceNum) / ivNum) * 100;
      isPricedRight = actualMargin >= 40;
    }
    
    return {
      intrinsicValue: ivNum,
      maxBuyPrice,
      marginPercent,
      currentPrice: priceNum || null,
      actualMargin,
      isPricedRight,
      upside: priceNum ? ((ivNum - priceNum) / priceNum) * 100 : null
    };
  }, [intrinsicValue, currentPrice]);

  // Gauge component
  const Gauge = ({ value, max = 100 }) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    const getColor = () => {
      if (value >= 40) return 'bg-emerald-500';
      if (value >= 20) return 'bg-yellow-500';
      return 'bg-red-500';
    };
    
    return (
      <div className="relative h-4 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`absolute left-0 top-0 h-full ${getColor()} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
        {/* 40% marker */}
        <div 
          className="absolute top-0 h-full w-0.5 bg-white/50"
          style={{ left: '40%' }}
        />
        <span className="absolute left-[40%] -top-5 text-xs text-gray-400 -translate-x-1/2">40%</span>
      </div>
    );
  };

  return (
    <Card className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white">
          <Shield className="w-5 h-5 text-blue-400" />
          Calculateur Marge de Sécurité
        </CardTitle>
        <p className="text-sm text-gray-400">
          Déterminez le prix maximum d'achat pour une marge de sécurité de 40% (méthode Benjamin Graham)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Valeur intrinsèque estimée</label>
            <Input
              type="number"
              placeholder="Ex: 100"
              value={intrinsicValue}
              onChange={(e) => setIntrinsicValue(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Via DCF, multiples, ou autre méthode</p>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Prix actuel (optionnel)</label>
            <Input
              type="number"
              placeholder="Ex: 65"
              value={currentPrice}
              onChange={(e) => setCurrentPrice(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Pour évaluer l'opportunité</p>
          </div>
        </div>

        {result && (
          <div className="mt-6 space-y-4">
            {/* Main results */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900/50 rounded-lg border border-white/10">
                <p className="text-sm text-gray-400 mb-1">Valeur intrinsèque</p>
                <p className="text-2xl font-bold text-white">{result.intrinsicValue.toFixed(2)}</p>
              </div>
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-emerald-400" />
                  <p className="text-sm text-emerald-400">Prix max d'achat</p>
                </div>
                <p className="text-2xl font-bold text-emerald-400">{result.maxBuyPrice.toFixed(2)}</p>
                <p className="text-xs text-gray-400">= Valeur × 60%</p>
              </div>
            </div>

            {/* Marge de sécurité explanation */}
            <div className="p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-blue-400" />
                <p className="text-sm font-medium text-white">Marge de sécurité : {result.marginPercent}%</p>
              </div>
              <p className="text-sm text-gray-300">
                En achetant à <strong className="text-emerald-400">{result.maxBuyPrice.toFixed(2)}</strong> ou moins,
                vous conservez une marge de sécurité de 40% par rapport à votre estimation de valeur intrinsèque.
              </p>
            </div>

            {/* If current price is provided */}
            {result.currentPrice && (
              <div className={`p-4 rounded-lg border ${
                result.isPricedRight 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-amber-500/10 border-amber-500/30'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-400">Marge de sécurité actuelle</p>
                  <Badge className={result.isPricedRight 
                    ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                    : 'bg-amber-500/20 border-amber-500/30 text-amber-400'
                  }>
                    {result.isPricedRight ? '✓ Suffisante' : '⚠ Insuffisante'}
                  </Badge>
                </div>
                
                <div className="mb-4">
                  <Gauge value={result.actualMargin} />
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div>
                    <p className="text-gray-400">Prix actuel</p>
                    <p className="text-white font-medium">{result.currentPrice.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Marge actuelle</p>
                    <p className={`font-medium ${result.isPricedRight ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {result.actualMargin.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Potentiel hausse</p>
                    <p className={`font-medium ${result.upside > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {result.upside > 0 ? '+' : ''}{result.upside.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {!result.isPricedRight && (
                  <div className="mt-3 flex items-start gap-2 text-sm">
                    <TrendingDown className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-amber-300">
                      Le prix actuel ne respecte pas la marge de sécurité de 40%.
                      Attendez une baisse à <strong>{result.maxBuyPrice.toFixed(2)}</strong> ou moins.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Graham quote */}
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <blockquote className="text-blue-300 italic">
                    "Le prix est ce que vous payez, la valeur est ce que vous obtenez."
                  </blockquote>
                  <p className="text-sm text-blue-400 mt-1">— Benjamin Graham, L'Investisseur Intelligent</p>
                  <p className="text-xs text-gray-400 mt-2">
                    La marge de sécurité protège contre les erreurs d'estimation et les imprévus du marché.
                    Graham recommandait 40% minimum pour les investisseurs défensifs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarginOfSafetyCalculator;
